import styled from 'styled-components';
import { JoinInput, JoinPhoneInput } from '../items/joinInput';
import { InputBtn } from '../items/inputBtn';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '../items/Button';
import { ComplateFormFailed } from './complate.form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { forgotIdSchema } from '../../schema/forgotSchema';
import { formatPhoneNumber } from '../../utils/util';
import { requestPassidAuth, verifyPassidAuth } from '../../api/join';
import { AlertModal } from '../modal/alert.modal';

interface ForgotIdProps {
  selectMenuHandler: (index: number) => void;
}

export const ForgotId = ({ selectMenuHandler }: ForgotIdProps) => {
  const [viewChange, setViewChange] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [forgetId, setForgetId] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [phoneAuthUUID, setPhoneAuthUUID] = useState<string | null>(null); // 인증 UUID 상태 추가
  const [isPhoneVerified, setIsPhoneVerified] = useState(false); // 전화번호 인증 여부 상태 추가
  const [isPhoneDisabled, setIsPhoneDisabled] = useState(false);
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null); // 타이머 상태 추가
  const [remainingTime, setRemainingTime] = useState(180); // 남은 시간(초) 상태 추가
  const history = useHistory();

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(forgotIdSchema),
  });

  const goLogin = () => {
    history.push('/login');
  };

  const handleNextClick = (type: boolean) => {
    setIsSuccess(type);
    setViewChange(true);
  };

  const startTimer = () => {
    if (timer) {
      clearTimeout(timer);
    }

    setRemainingTime(180); // 타이머 시작 시 3분 설정

    const newTimer = setInterval(() => {
      setRemainingTime((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(newTimer);
          setPhoneAuthUUID(null);
          setModalMessage('인증 시간이 만료되었습니다. 다시 시도해주세요.');
          setIsModalOpen(true);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000); // 1초마다 타이머 업데이트

    setTimer(newTimer);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const phoneAuth = async () => {
    try {
      const phone = getValues('phone');
      if (phone) {
        const uuid = await requestPassidAuth(phone);
        setPhoneAuthUUID(uuid);
        setModalMessage('인증번호가 전송되었습니다. 번호를 확인해주세요.');
        setIsModalOpen(true);
        startTimer(); // 타이머 시작
      } else {
        setModalMessage('휴대폰 번호를 입력해주세요.');
        setIsModalOpen(true);
      }
    } catch (error: any) {
      if (error.response && error.response.status === 409) {
        setModalMessage(
          '요청하신 핸드폰은 회원가입이 되어있지 않습니다. 회원가입을 진행해주세요.',
        );
      } else {
        console.error('휴대폰 본인 인증 요청 실패:', error);
        setModalMessage('본인 인증 요청에 실패했습니다.');
      }
      setIsModalOpen(true);
    }
  };

  const verifyAuthCode = async (authCode: string) => {
    if (!phoneAuthUUID) {
      setModalMessage('인증 시간이 만료되었습니다. 다시 시도해주세요.');
      setIsModalOpen(true);
      return;
    }

    try {
      const forgotId = await verifyPassidAuth(phoneAuthUUID, authCode);
      console.log(forgotId);
      setModalMessage('본인 인증에 성공하였습니다.');
      setIsModalOpen(true);
      setIsPhoneVerified(true);
      setIsPhoneDisabled(true);
      setForgetId(forgotId);
      if (timer) {
        clearTimeout(timer); // 인증 성공 시 타이머 해제
      }
    } catch (error) {
      console.error('인증 실패:', error);
      setModalMessage('본인 인증 요청에 실패하였습니다.');
      setIsModalOpen(true);
    }
  };

  const onSubmit = (data: any) => {
    // 여기서 본인인증 처리 로직을 넣습니다.

    if (!isPhoneVerified) {
      setModalMessage('휴대폰 번호 인증을 완료해주세요.');
      setIsModalOpen(true);
      return; // 인증이 완료되지 않은 경우 회원가입 진행 중단
    }
    if (isSuccess) {
      handleNextClick(true); // 성공 시
    } else {
      handleNextClick(false); // 성공 시
    }
  };

  return (
    <Wrapper>
      {viewChange ? (
        isSuccess ? (
          <div className="complate-form">
            <h4>입력한 정보와 일치하는 아이디입니다.</h4>
            <div className="id-box">
              <p>{forgetId}</p>
              {/* <span>2024.06.04 가입</span> */}
            </div>
            <span className="go-password" onClick={() => selectMenuHandler(1)}>
              비밀번호 찾기
            </span>
            <Button title="로그인하기" onClick={goLogin} fiexd />
          </div>
        ) : (
          <>
            <ComplateFormFailed />
            <Button title="로그인 화면으로 이동" onClick={goLogin} fiexd />
          </>
        )
      ) : (
        <div className="forgot-form">
          <h4>가입 당시 등록한 휴대폰 번호를 입력해주세요.</h4>
          <form onSubmit={handleSubmit(onSubmit)}>
            <JoinPhoneInput
              name="phone"
              label="휴대폰 번호"
              placeholder="휴대폰 번호 입력"
              type="text"
              btn
              btnTitle="본인인증"
              disabled={isPhoneDisabled}
              errors={
                errors.phone?.message ? [errors.phone.message as string] : []
              }
              register={register}
              validation={{
                onChange: (e: any) => {
                  const formattedValue = formatPhoneNumber(e.target.value);
                  setValue('phone', formattedValue, { shouldValidate: true });
                },
              }}
              onClick={phoneAuth}
              className={`input-disabled ${isPhoneDisabled ? 'disabled' : ''}`}
            />
            <div className="timer-wrapper">
              <JoinInput
                name="authCode"
                label="인증번호"
                placeholder="인증번호 입력"
                type="text"
                btn
                btnTitle="인증확인"
                register={register}
                onClick={() => verifyAuthCode(getValues('authCode') || '')}
              />
              <p>
                남은 시간: {Math.floor(remainingTime / 60)}:
                {remainingTime % 60 < 10
                  ? `0${remainingTime % 60}`
                  : remainingTime % 60}
              </p>
            </div>
            <InputBtn name="다 음" type="submit" />
          </form>
        </div>
      )}
      {isModalOpen && (
        <AlertModal
          handleConfirmClose={handleModalClose}
          confirmText={modalMessage}
          onClick={handleModalClose} // 회원가입 완료 시에만 로그인 화면으로 이동
        />
      )}
    </Wrapper>
  );
};

//style
export const Wrapper = styled.div`
  h4 {
    font-size: 1.4rem;
    font-weight: 400;
    color: ${(props) => props.theme.gray500};
  }
  .forgot-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    form {
      display: flex;
      flex-direction: column;
      gap: 1.2rem;
      .input-disabled {
        position: relative;
        &.disabled {
          opacity: 0.4;
          > div {
            background-color: #f0f0f0;
          }
          &::after {
            content: '';
            top: 0;
            left: 0;
            position: absolute;
            width: 100%;
            height: 100%;
            background-color: black;
            opacity: 0.2;
            border-radius: 0.8rem;
          }
        }
      }
      .timer-wrapper {
        position: relative;
        p {
          position: absolute;
          bottom: 2rem;
          right: 8rem;
          font-size: 1.3rem;
          width: 8.5rem;
        }
      }
    }
  }
  .complate-form {
    padding: 0 2rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    h4 {
      text-align: center;
    }
    .id-box {
      min-height: 10rem;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 1.2rem;
      border-radius: 0.8rem;
      background-color: ${(props) => props.theme.idBoxBgColor};
      p {
        font-size: 1.6rem;
        font-weight: 600;
      }
      span {
        font-size: 1.3rem;
        font-weight: 400;
        line-height: 1.6rem;
        color: ${(props) => props.theme.gray900};
      }
    }
  }
  .go-password {
    color: ${(props) => props.theme.btnBlueColor};
    display: flex;
    font-size: 1.2rem;
    font-weight: 400;
    padding-left: 0.6rem;
  }
  .password-complate-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    form {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }
  }
`;
